import React, { useEffect, useState } from "react";
import { api, API, fmtUSD, fmtDate } from "../lib/api";
import LayerBadge from "../components/LayerBadge";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Download, Undo2, BookOpenCheck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  projected: { txt: "Projected", style: "bg-[#e0f2fe] text-[#0369a1]" },
  in_progress: { txt: "In Progress", style: "bg-[#fef3c7] text-[#92400e]" },
  realized: { txt: "Realized", style: "bg-[#dcfce7] text-[#15803d]" },
  reversed: { txt: "Reversed", style: "bg-[#fee2e2] text-[#b91c1c]" },
};

function LedgerStatusPill({ status }) {
  const m = STATUS_STYLES[status] || { txt: status, style: "bg-secondary" };
  return (
    <span className={`px-2 py-0.5 rounded-sm text-[10px] font-semibold uppercase tracking-[0.1em] whitespace-nowrap ${m.style}`}>
      {m.txt}
    </span>
  );
}

function Kpi({ label, value, sub, accent, testId }) {
  return (
    <div className="cs-card p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      <div className={`cs-kpi-value text-2xl mt-1 ${accent ? "text-[#00579e]" : "text-foreground"}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function SavingsLedger() {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [reversing, setReversing] = useState(null);
  const [reverseReason, setReverseReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.ledger.summary(),
        api.ledger.entries(statusFilter === "all" ? null : statusFilter),
      ]);
      setSummary(s);
      setEntries(e || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const reverse = async () => {
    if (!reversing) return;
    try {
      await api.ledger.reverse(reversing.id, reverseReason || "Fix reverted or regressed");
      toast.success("Entry reversed", { description: "Realized savings removed and the finding re-opened." });
      setReversing(null);
      setReverseReason("");
      load();
    } catch (e) {
      toast.error("Reverse failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const t = summary?.totals_monthly_usd || {};

  return (
    <div className="px-6 lg:px-10 py-8 space-y-6" data-testid="savings-ledger-page">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Kpi testId="ledger-kpi-projected" label="Projected Pipeline" value={fmtUSD(t.projected)} sub={`${summary?.counts?.projected ?? 0} entries · awaiting approval`} />
        <Kpi testId="ledger-kpi-inprogress" label="In Progress" value={fmtUSD(t.in_progress)} sub={`${summary?.counts?.in_progress ?? 0} approved, executing`} />
        <Kpi testId="ledger-kpi-realized" label="Realized / mo" value={fmtUSD(t.realized)} accent sub={`${summary?.counts?.realized ?? 0} verified fixes`} />
        <Kpi testId="ledger-kpi-annualized" label="Realized Annualized" value={fmtUSD(summary?.realized_annualized_usd)} accent sub="12 × monthly realized" />
        <Kpi testId="ledger-kpi-reversed" label="Reversed" value={summary?.counts?.reversed ?? 0} sub={`attribution integrity`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly projected vs realized */}
        <div className="cs-card p-5" data-testid="ledger-monthly-chart">
          <div className="flex items-center gap-2 mb-4">
            <BookOpenCheck className="w-4 h-4 text-[#00579e]" />
            <h2 className="cs-heading text-base font-semibold">Projected vs Realized by Month</h2>
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={summary?.monthly || []}>
              <XAxis dataKey="month" fontSize={11} />
              <YAxis tickFormatter={(v) => fmtUSD(v, { compact: true })} fontSize={11} />
              <Tooltip formatter={(v) => fmtUSD(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="projected" name="Projected" fill="#94a3b8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="realized" name="Realized" fill="#00579e" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* By team */}
        <div className="cs-card p-5" data-testid="ledger-by-team">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="w-4 h-4 text-[#00579e]" />
            <h2 className="cs-heading text-base font-semibold">Savings by Team</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Team</th>
                <th className="py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Entries</th>
                <th className="py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Projected</th>
                <th className="py-2 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Realized</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.by_team || []).map((r) => (
                <tr key={r.team} className="border-b border-border last:border-0">
                  <td className="py-2 font-medium">{r.team}</td>
                  <td className="py-2 text-right">{r.count}</td>
                  <td className="py-2 text-right cs-mono text-muted-foreground">{fmtUSD(r.projected)}</td>
                  <td className="py-2 text-right cs-mono font-semibold text-[#15803d]">{fmtUSD(r.realized)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Entries */}
      <div className="cs-card overflow-hidden" data-testid="ledger-entries-card">
        <div className="px-4 py-3 border-b border-border flex flex-wrap items-center gap-2">
          <h2 className="cs-heading text-base font-semibold">Ledger Entries</h2>
          <div className="flex gap-1 ml-2">
            {["all", "projected", "in_progress", "realized", "reversed"].map((s) => (
              <button
                key={s}
                data-testid={`ledger-filter-${s}`}
                onClick={() => setStatusFilter(s)}
                className={`px-2.5 py-1 rounded-sm text-xs transition-colors duration-200 ${
                  statusFilter === s ? "bg-[#00579e] text-white" : "bg-[hsl(var(--cs-tint))] text-muted-foreground hover:text-foreground"
                }`}
              >
                {s === "all" ? "All" : STATUS_STYLES[s]?.txt || s}
              </button>
            ))}
          </div>
          <a
            href={`${API}/ledger/export/focus`}
            data-testid="ledger-export-focus"
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-border text-xs font-medium hover:bg-[hsl(var(--cs-tint))] transition-colors duration-200"
          >
            <Download className="w-3.5 h-3.5" /> Export FOCUS CSV
          </a>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Action / Resource</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Service</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Layer</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Team</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Projected / mo</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground text-right">Realized / mo</th>
                <th className="px-4 py-3 text-[10px] uppercase tracking-[0.14em] font-medium text-muted-foreground">Booked</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground" data-testid="no-ledger-entries">No ledger entries yet — approve & execute a remediation to book savings.</td></tr>
              )}
              {!loading && entries.map((e) => (
                <tr key={e.id} className="cs-row-hover border-b border-border last:border-0" data-testid={`ledger-row-${e.id}`}>
                  <td className="px-4 py-3"><LedgerStatusPill status={e.status} /></td>
                  <td className="px-4 py-3">
                    <div className="cs-mono text-[12px] font-medium">{e.action}</div>
                    <div className="text-[11px] text-muted-foreground truncate max-w-[200px]" title={e.resource_name}>{e.resource_name}</div>
                    {e.verification?.evidence_note && (
                      <div className="text-[10px] text-muted-foreground mt-0.5 italic max-w-[260px] truncate" title={e.verification.evidence_note}>
                        {e.verification.method === "billing_export_counterfactual" ? "✓ verified vs billing baseline" : "simulated verification"}
                      </div>
                    )}
                    {e.reversal_reason && (
                      <div className="text-[10px] text-[#b91c1c] mt-0.5">↩ {e.reversal_reason}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs">{e.service}</td>
                  <td className="px-4 py-3"><LayerBadge layer={e.layer} /></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{e.team}</td>
                  <td className="px-4 py-3 text-right cs-mono text-muted-foreground">{fmtUSD(e.projected_monthly_savings_usd)}</td>
                  <td className="px-4 py-3 text-right cs-mono font-semibold text-[#15803d]">
                    {e.status === "realized" ? fmtUSD(e.realized_monthly_savings_usd) : "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(e.booked_at)}</td>
                  <td className="px-4 py-3 text-right">
                    {e.status === "realized" && (
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`reverse-${e.id}`}
                        onClick={() => setReversing(e)}
                        className="rounded-sm h-7 text-xs border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444] hover:text-white"
                      >
                        <Undo2 className="w-3 h-3 mr-1" /> Reverse
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reverse modal */}
      <Dialog open={!!reversing} onOpenChange={(v) => !v && setReversing(null)}>
        <DialogContent className="rounded-sm border-border" data-testid="reverse-modal">
          <DialogHeader>
            <DialogTitle className="cs-heading">Reverse ledger entry</DialogTitle>
            <DialogDescription>
              The realized savings will be removed and the underlying finding re-opened
              (attribution integrity). Use this when a fix was reverted or regressed.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reverseReason}
            onChange={(e) => setReverseReason(e.target.value)}
            placeholder="e.g., Terraform change reverted in PR #482 after on-call escalation"
            data-testid="reverse-reason-textarea"
            className="rounded-sm"
            rows={3}
          />
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setReversing(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={reverse} data-testid="confirm-reverse" className="bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-sm">
              Reverse Entry
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
